/**
 * AkountFly - Display Labels (Sprint 1, refactored to unified single view)
 * ========================================================================
 *
 * AkountFly ships a SINGLE unified UI - there is no Business/Accountant
 * toggle. The labels below are the canonical user-facing strings used by
 * `getLabel()` across the app. They were chosen to remove jargon for
 * non-accountants while keeping professional terms in parentheses where
 * accountants still need them visible.
 *
 * Pattern: friendly first, accountant term in parentheses when both are
 * useful. The sidebar uses single short labels; page titles and reports
 * are free to use the longer "Friendly (Formal)" pattern.
 *
 * Adding a new label: append a `key: 'Visible label'` entry below.
 */

// DISPLAY_MODES kept for backwards-compat with the legacy hook surface.
// The app now always behaves as if mode === BUSINESS.
export const DISPLAY_MODES = {
  BUSINESS: 'business',
  ACCOUNTANT: 'accountant',
};

// Canonical single-view labels. `getLabel(key)` returns these verbatim.
// Keys keep their accountant-canonical spelling so existing call-sites
// (and future codemods touching <h1>{getLabel('Trial Balance')}</h1>) keep
// working without rewriting every page.
export const LABELS = {
  // Primary navigation
  'Home':                    'Home',
  'Invoices':                'Invoices',
  'Bills':                   'Bills',
  'Insights':                'Insights',
  'Ask AkountFly':           'Ask AkountFly',
  'Bank Accounts':           'Bank Accounts',
  'Transactions':            'Transactions',
  'Reports':                 'Reports',
  'Budgets':                 'Budgets',
  'Products & Customers':    'Products & Contacts',
  'More Tools':              'More Tools',

  // Personal navigation
  'Personal Dashboard':      'Dashboard',
  'Income':                  'Income',
  'Expenses':                'Expenses',
  'Personal Budget':         'Personal Budget',
  'Sources & Payees':        'Sources & Payees',
  'Personal Reports':        'Personal Reports',
  'Account Settings':        'Account Settings',

  // Page titles & accounting terms (friendly + formal in parens)
  'Chart of Accounts':       'Categories (Chart of Accounts)',
  'Manual Journals':         'Adjustments (Manual Journals)',
  'Recurring Journals':      'Repeating Entries (Recurring Journals)',
  'System Journals':         'Auto Entries (System Journals)',
  'General Ledger':          'All Transactions (General Ledger)',
  'Trial Balance':           'Account Totals (Trial Balance)',
  'Opening Balances':        'Starting Balances (Opening Balances)',
  'Period Close':            'Lock the Month (Period Close)',
  'Reconciliation':          'Match Transactions (Reconciliation)',
  'Balance Sheet':           'Money Position (Balance Sheet)',
  'Cash Flow':               'Cashflow Statement',
  'Profit & Loss':           'Profit & Loss (Income Statement)',

  // Inline accounting concepts (used inline & in HelpTip)
  'Debit':                   'Debit',
  'Credit':                  'Credit',
  'Accounts Receivable':     'Money Owed to You (Accounts Receivable)',
  'Accounts Payable':        'Money You Owe (Accounts Payable)',
  'Revenue':                 'Revenue',
  'COGS':                    'Cost of Goods Sold',
  'Assets':                  'Assets',
  'Liabilities':             'Liabilities',
  'Equity':                  'Equity (Net Worth)',
  'Accrual':                 'Accrual',
  'Cash Basis':              'Cash Basis',
  'Fixed Assets':            'Equipment & Property (Fixed Assets)',
  'Deferred Revenue':        'Prepaid by Customers (Deferred Revenue)',
  'Prepaid Expenses':        'Paid in Advance (Prepaid Expenses)',
  'FX Revaluation':          'Foreign Currency Adjustment',
};

// Reports surface - single unified list shown to every user. ReportsPage
// can choose to group / collapse by accountant relevance internally.
export const REPORTS_LIST = [
  'Profit & Loss',
  'Balance Sheet',
  'Cash Flow',
  'Trial Balance',
  'General Ledger',
  'Invoices Aging',
  'Bills Aging',
  'Bank Summary',
  'VAT/GST Report',
  'WHT Report',
  'Journal Report',
  'Audit Trail',
  'Tax Summary',
];

// Legacy alias kept so existing imports keep resolving.
export const ACCOUNTANT_ONLY_FEATURES = [];
export const MODE_REPORTS = { business: REPORTS_LIST, accountant: REPORTS_LIST };

// Plain-English glossary entries used by future <HelpTip> tooltips.
// Same content regardless of mode - applies to every viewer.
export const GLOSSARY = {
  'Debit':                { short: 'Increases an asset/expense or decreases a liability/income.' },
  'Credit':               { short: 'Decreases an asset/expense or increases a liability/income.' },
  'Accrual':              { short: 'Recorded when earned/incurred, not when paid.' },
  'Cash Basis':           { short: 'Recorded only when money actually moves.' },
  'Accounts Receivable':  { short: 'Money customers owe you from invoices not yet paid.' },
  'Accounts Payable':     { short: 'Money you owe suppliers from bills not yet paid.' },
  'Trial Balance':        { short: 'A check that every debit equals every credit.' },
  'General Ledger':       { short: 'Every transaction grouped by account.' },
  'Chart of Accounts':    { short: 'The list of buckets we use to organise your money.' },
  'Reconciliation':       { short: 'Matching your records to the bank statement.' },
  'Opening Balances':     { short: 'Where each account stood when you started using AkountFly.' },
  'Period Close':         { short: 'Locking a month so no more entries can change it.' },
  'Fixed Assets':         { short: 'Things you bought that last more than a year.' },
  'Deferred Revenue':     { short: "Money customers paid you for work you haven't delivered yet." },
  'Prepaid Expenses':     { short: 'Expenses you paid for in advance. Recognised over time.' },
  'WHT':                  { short: 'Tax held back at the time of payment and remitted to the tax authority.' },
  'VAT':                  { short: 'Sales tax added to invoices; you remit the net to the tax authority.' },
  'FX Revaluation':       { short: 'Adjusting foreign-currency balances to current rates.' },
};
